import {
  type FotoComprimida,
  type Ubicacion,
  comprimirArchivo,
  comprimirFuente,
} from '@/lib/fotos';
import { cn } from '@/lib/utils';
import {
  ArrowsClockwise,
  Check,
  CircleNotch,
  Lightning,
  LightningSlash,
  MapPin,
  X,
} from '@phosphor-icons/react';
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Cámara dentro de la app (como Remato): ráfaga de hasta `max` fotos, sin
 * pasar por la galería del teléfono. Cada foto se comprime al capturarla y
 * lleva la última ubicación GPS conocida.
 *
 * Usa getUserMedia; si no está disponible o se niega el permiso, ofrece la
 * cámara nativa del teléfono (input file) como respaldo.
 */

export interface Captura {
  foto: FotoComprimida;
  tomada_en: string;
  ubicacion: Ubicacion | null;
  /** URL local de la miniatura para la bandeja (se libera al cerrar). */
  vista: string;
}

interface Props {
  /** Texto del destino (ej. "VC-F.04 · Colado Losa"); vacío = se elige al terminar. */
  destino?: string;
  max?: number;
  onListo: (capturas: Captura[]) => void;
  onCerrar: () => void;
}

interface Capacidades {
  torch?: boolean;
  zoom?: { min: number; max: number };
}

export function CamaraRafaga({ destino, max = 10, onListo, onCerrar }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const ubicacionRef = useRef<Ubicacion | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [frontal, setFrontal] = useState(false);
  const [capturas, setCapturas] = useState<Captura[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [listo, setListo] = useState(false);
  const [disparando, setDisparando] = useState(false);
  const [destello, setDestello] = useState(false);
  const [caps, setCaps] = useState<Capacidades>({});
  const [linterna, setLinterna] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [conGps, setConGps] = useState(false);

  const detener = useCallback(() => {
    for (const t of streamRef.current?.getTracks() ?? []) t.stop();
    streamRef.current = null;
  }, []);

  // Cámara
  useEffect(() => {
    let cancelado = false;
    async function abrir() {
      setListo(false);
      setError(null);
      detener();
      if (!navigator.mediaDevices?.getUserMedia) {
        setError('Este navegador no permite usar la cámara dentro de la app.');
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: {
            facingMode: { ideal: frontal ? 'user' : 'environment' },
            width: { ideal: 2560 },
            height: { ideal: 1920 },
          },
        });
        if (cancelado) {
          for (const t of stream.getTracks()) t.stop();
          return;
        }
        streamRef.current = stream;
        const video = videoRef.current;
        if (video) {
          video.srcObject = stream;
          await video.play().catch(() => undefined);
        }
        const track = stream.getVideoTracks()[0];
        const c = (track?.getCapabilities?.() ?? {}) as MediaTrackCapabilities & {
          torch?: boolean;
          zoom?: { min: number; max: number };
        };
        setCaps({
          torch: !!c.torch,
          zoom: c.zoom ? { min: c.zoom.min, max: c.zoom.max } : undefined,
        });
        setLinterna(false);
        setZoom(1);
        setListo(true);
      } catch (e) {
        const nombre = e instanceof DOMException ? e.name : '';
        setError(
          nombre === 'NotAllowedError'
            ? 'No se dio permiso para usar la cámara.'
            : 'No se pudo abrir la cámara.',
        );
      }
    }
    void abrir();
    return () => {
      cancelado = true;
      detener();
    };
  }, [frontal, detener]);

  // GPS mientras la cámara está abierta
  useEffect(() => {
    if (!navigator.geolocation) return;
    const id = navigator.geolocation.watchPosition(
      (p) => {
        ubicacionRef.current = {
          latitud: p.coords.latitude,
          longitud: p.coords.longitude,
          precision_m: p.coords.accuracy,
        };
        setConGps(true);
      },
      () => setConGps(false),
      { enableHighAccuracy: true, maximumAge: 30_000, timeout: 20_000 },
    );
    return () => navigator.geolocation.clearWatch(id);
  }, []);

  // Liberar las miniaturas locales al desmontar
  useEffect(
    () => () => {
      for (const c of capturasRef.current) URL.revokeObjectURL(c.vista);
    },
    [],
  );
  const capturasRef = useRef<Captura[]>([]);
  capturasRef.current = capturas;

  async function aplicar(constraint: Record<string, unknown>) {
    const track = streamRef.current?.getVideoTracks()[0];
    await track
      ?.applyConstraints({ advanced: [constraint as MediaTrackConstraintSet] })
      .catch(() => undefined);
  }

  async function disparar() {
    const video = videoRef.current;
    if (!video || !listo || disparando || capturas.length >= max) return;
    setDisparando(true);
    setDestello(true);
    setTimeout(() => setDestello(false), 120);
    try {
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('canvas');
      if (frontal) {
        ctx.translate(canvas.width, 0);
        ctx.scale(-1, 1);
      }
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const foto = await comprimirFuente(canvas);
      agregar(foto);
    } finally {
      setDisparando(false);
    }
  }

  function agregar(foto: FotoComprimida) {
    setCapturas((prev) => [
      ...prev,
      {
        foto,
        tomada_en: new Date().toISOString(),
        ubicacion: ubicacionRef.current,
        vista: URL.createObjectURL(foto.mini),
      },
    ]);
  }

  async function desdeArchivos(e: React.ChangeEvent<HTMLInputElement>) {
    const archivos = Array.from(e.target.files ?? []).slice(0, max - capturas.length);
    e.target.value = '';
    for (const a of archivos) agregar(await comprimirArchivo(a));
  }

  function quitar(i: number) {
    setCapturas((prev) => {
      const c = prev[i];
      if (c) URL.revokeObjectURL(c.vista);
      return prev.filter((_, k) => k !== i);
    });
  }

  function cerrar() {
    if (capturas.length > 0 && !window.confirm(`¿Descartar ${capturas.length} foto(s)?`)) return;
    onCerrar();
  }

  function terminar() {
    detener();
    // Las vistas se reutilizan en el diálogo de destino; el receptor las libera.
    capturasRef.current = [];
    onListo(capturas);
  }

  const zoomOpciones = caps.zoom
    ? [
        caps.zoom.min < 1 ? caps.zoom.min : null,
        1,
        caps.zoom.max >= 2 ? 2 : null,
        caps.zoom.max >= 5 ? 5 : null,
      ].filter((z): z is number => z !== null)
    : [];
  const lleno = capturas.length >= max;

  return (
    <div className="fixed inset-0 z-[60] flex flex-col bg-black text-white">
      {/* Barra superior */}
      <div className="flex items-center justify-between px-3 pb-2 pt-[calc(0.5rem+env(safe-area-inset-top))]">
        <button
          type="button"
          onClick={cerrar}
          className="rounded-full bg-white/15 p-2.5"
          aria-label="Cerrar cámara"
        >
          <X className="h-5 w-5" weight="bold" />
        </button>
        <div className="min-w-0 px-2 text-center">
          <p className="truncate text-xs font-medium">{destino || 'Elegirá la obra al terminar'}</p>
          <p className="flex items-center justify-center gap-1 text-[10px] text-white/70">
            <MapPin
              className={cn('h-3 w-3', conGps ? 'text-adelante-primary' : 'text-white/40')}
              weight="fill"
            />
            {conGps ? 'GPS activo' : 'Sin GPS'}
          </p>
        </div>
        <div className="flex gap-2">
          {caps.torch && (
            <button
              type="button"
              onClick={() => {
                const v = !linterna;
                setLinterna(v);
                void aplicar({ torch: v });
              }}
              className={cn(
                'rounded-full p-2.5',
                linterna ? 'bg-yellow-400 text-black' : 'bg-white/15',
              )}
              aria-label="Flash"
            >
              {linterna ? (
                <Lightning className="h-5 w-5" weight="fill" />
              ) : (
                <LightningSlash className="h-5 w-5" weight="bold" />
              )}
            </button>
          )}
          <button
            type="button"
            onClick={() => setFrontal((f) => !f)}
            className="rounded-full bg-white/15 p-2.5"
            aria-label="Cambiar cámara"
          >
            <ArrowsClockwise className="h-5 w-5" weight="bold" />
          </button>
        </div>
      </div>

      {/* Visor */}
      <div className="relative flex-1 overflow-hidden">
        <video
          ref={videoRef}
          playsInline
          muted
          autoPlay
          className={cn('h-full w-full object-cover', frontal && '-scale-x-100')}
        />
        {destello && <div className="absolute inset-0 bg-white/80" />}
        {!listo && !error && (
          <div className="absolute inset-0 flex items-center justify-center">
            <CircleNotch className="h-8 w-8 animate-spin" weight="bold" />
          </div>
        )}
        {error && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-6 text-center">
            <p className="text-sm">{error}</p>
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="rounded-md bg-white px-4 py-2 text-sm font-semibold text-black"
            >
              Usar la cámara del teléfono
            </button>
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          capture="environment"
          multiple
          className="hidden"
          onChange={desdeArchivos}
        />
        {zoomOpciones.length > 1 && (
          <div className="absolute inset-x-0 bottom-3 flex justify-center gap-2">
            {zoomOpciones.map((z) => (
              <button
                key={z}
                type="button"
                onClick={() => {
                  setZoom(z);
                  void aplicar({ zoom: z });
                }}
                className={cn(
                  'h-9 min-w-9 rounded-full px-2 text-xs font-semibold',
                  zoom === z ? 'bg-yellow-400 text-black' : 'bg-black/50',
                )}
              >
                {z < 1 ? z.toFixed(1) : z}x
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Bandeja */}
      <div className="flex h-20 items-center gap-2 overflow-x-auto px-3">
        {capturas.map((c, i) => (
          <div key={c.vista} className="relative shrink-0">
            <img
              src={c.vista}
              alt={`Foto ${i + 1}`}
              className="h-16 w-16 rounded-md border-2 border-white/80 object-cover"
            />
            <button
              type="button"
              onClick={() => quitar(i)}
              className="absolute -right-1.5 -top-1.5 rounded-full bg-black p-0.5"
              aria-label="Quitar foto"
            >
              <X className="h-3.5 w-3.5" weight="bold" />
            </button>
          </div>
        ))}
        {capturas.length === 0 && (
          <p className="w-full text-center text-xs text-white/60">Hasta {max} fotos por ráfaga</p>
        )}
      </div>

      {/* Controles */}
      <div className="flex items-center justify-between px-8 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-2">
        <span className="w-14 text-center text-sm tabular-nums text-white/80">
          {capturas.length}/{max}
        </span>
        <button
          type="button"
          onClick={disparar}
          disabled={!listo || lleno || disparando}
          className="flex h-20 w-20 items-center justify-center rounded-full border-4 border-white disabled:opacity-40"
          aria-label="Tomar foto"
        >
          <span
            className={cn(
              'h-16 w-16 rounded-full bg-white transition-transform',
              disparando && 'scale-90',
            )}
          />
        </button>
        <button
          type="button"
          onClick={terminar}
          disabled={capturas.length === 0}
          className="flex h-14 w-14 items-center justify-center rounded-full bg-adelante-primary text-black disabled:opacity-30"
          aria-label="Terminar"
        >
          <Check className="h-7 w-7" weight="bold" />
        </button>
      </div>
      {lleno && (
        <p className="pb-2 text-center text-xs text-yellow-300">
          Máximo {max} fotos: toque ✓ para guardarlas y siga con otra ráfaga.
        </p>
      )}
    </div>
  );
}
